import type { ApplicationPublic, GrantPublic, UserPublic } from '@accounts/shared';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { STR } from '../../i18n';
import {
  ApiError,
  bulkGrant,
  createGrant,
  listApplications,
  listGrants,
  listUsers,
  revokeGrant,
} from '../../lib/api';
import { grantedLine, namesById, revokedLine } from '../../lib/grants';
import { healthOf } from '../../lib/health';
import { queryKeys } from '../../lib/queryKeys';
import { showToast } from '../../lib/toast';
import { Modal, ModalParagraph, ModalProse } from '../../ui/Modal';
import { Card, Chip, Dot, ListTruncationNotice, MasterDetailSkeleton } from '../../ui/Primitives';
import {
  detailColumnStyle,
  detailNameStyle,
  detailPaneStyle,
  eyebrowStyle,
  listPaneHeaderStyle,
  listPaneStyle,
  listContainerStyle,
  listRowStyle,
  masterDetailStyle,
  modalButtonStyle,
  monoStyle,
  primaryButtonStyle,
  quietButtonStyle,
  rowActionStyle,
  searchFieldStyle,
} from '../../ui/styles';

import { AdminShell } from './AdminShell';
import { GrantModal, RevokeModal } from './SharedModals';

type View = 'person' | 'application';

type Dialog =
  | { kind: 'grant' }
  | { kind: 'revoke'; grant: GrantPublic }
  | { kind: 'bulkGrant'; application: ApplicationPublic };

const LIST_LIMIT = 200;

/**
 * The Grants surface, both ways round. RETIRE, NEVER DELETE: a revoked grant is still a row,
 * so the revoked history renders muted and dated rather than disappearing — and it is muted
 * with a surface change and a neutral edge stripe, never with opacity, which would drop the
 * text under the AA contrast floor.
 *
 * The whole live-and-revoked graph is read once per application (`includeRevoked=true`) and
 * everything on this screen — both pickers, both counts, both lists — derives from it. The
 * grants endpoint requires exactly one of userId/applicationId, and applications are the
 * short side of this relation, so that is the query that costs least.
 */
export function GrantsPage() {
  const queryClient = useQueryClient();

  const [view, setView] = useState<View>('person');
  const [search, setSearch] = useState('');
  const [pickedUserId, setPickedUserId] = useState<string | null>(null);
  const [pickedApplicationId, setPickedApplicationId] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: queryKeys.users,
    queryFn: () => listUsers({ limit: LIST_LIMIT }),
  });
  const applicationsQuery = useQuery({
    queryKey: queryKeys.applications,
    queryFn: () => listApplications({ limit: LIST_LIMIT }),
  });

  const users = useMemo(() => usersQuery.data?.data ?? [], [usersQuery.data]);
  const applications = useMemo(
    () => applicationsQuery.data?.data ?? [],
    [applicationsQuery.data],
  );

  const grantQueries = useQueries({
    queries: applications.map((application) => ({
      queryKey: queryKeys.grantsForApplication(application.id, true),
      queryFn: () => listGrants({ applicationId: application.id, includeRevoked: true }),
    })),
  });

  const allGrants = useMemo(
    () => grantQueries.flatMap((result) => result.data?.data ?? []),
    [grantQueries],
  );
  const grantsLoading = grantQueries.some((result) => result.isLoading);
  const names = useMemo(() => namesById(users), [users]);

  /*
   * D3 — `meta.summary` is the COLLECTION-WIDE live/revoked split, identical on every one of
   * these per-application responses because the server computes it in SQL over the whole
   * grants table, not over the scoped rows it just returned. So the first response that has
   * arrived carries it, and nothing here counts grants off `allGrants` — that array is the
   * union of capped application lists and would under-report a large collection.
   */
  const grantsSummary =
    grantQueries.find((result) => result.data !== undefined)?.data?.meta.summary ?? null;

  /*
   * The master list on this screen is people or applications, so its truncation is theirs:
   * rows in hand versus each list's own `meta.total`. The grants themselves are unpaginated
   * (the query is always scoped to one subject), so there is no third denominator here.
   */
  const usersTotal = usersQuery.data?.meta.total ?? null;
  const applicationsTotal = applicationsQuery.data?.meta.total ?? null;

  const loading = usersQuery.isLoading || applicationsQuery.isLoading || grantsLoading;

  const needle = search.trim().toLowerCase();
  const visibleUsers = useMemo(
    () =>
      needle === ''
        ? users
        : users.filter((user) => `${user.name} ${user.email}`.toLowerCase().includes(needle)),
    [users, needle],
  );
  const visibleApplications = useMemo(
    () =>
      needle === ''
        ? applications
        : applications.filter((application) =>
            `${application.name} ${application.key}`.toLowerCase().includes(needle),
          ),
    [applications, needle],
  );

  const selectedUser: UserPublic | null =
    users.find((user) => user.id === pickedUserId) ?? visibleUsers[0] ?? null;
  const selectedApplication: ApplicationPublic | null =
    applications.find((application) => application.id === pickedApplicationId) ??
    visibleApplications[0] ??
    null;

  const subjectGrants = useMemo(() => {
    if (view === 'person') {
      if (selectedUser === null) return [];
      return allGrants.filter((grant) => grant.userId === selectedUser.id);
    }
    if (selectedApplication === null) return [];
    return allGrants.filter((grant) => grant.applicationId === selectedApplication.id);
  }, [view, allGrants, selectedUser, selectedApplication]);

  const liveGrants = subjectGrants.filter((grant) => grant.revokedAt === null);
  const pastGrants = subjectGrants.filter((grant) => grant.revokedAt !== null);

  // `active` mirrors `grantableUsers` below: a deactivated application cannot be granted into
  // (the server returns 400 `inactive_target`), so it must not be offered as if it could.
  const grantableApplications = applications.filter(
    (application) =>
      application.active && !liveGrants.some((grant) => grant.applicationId === application.id),
  );
  const grantableUsers = users.filter(
    (user) => user.active && !liveGrants.some((grant) => grant.userId === user.id),
  );
  const canGrant =
    view === 'person'
      ? selectedUser !== null && grantableApplications.length > 0
      : selectedApplication !== null && grantableUsers.length > 0;

  /*
   * By-application view only. `grantableUsers` above is already scoped to `liveGrants` for
   * the SELECTED application in this view, so it is exactly "active people who do not yet
   * hold this application" — the same set `canGrant` already uses. `allPeopleHold` already
   * covers the zero-candidates case with its own note, so this is deliberately not offered
   * again there — not offered, never offered-but-inert.
   */
  const canBulkGrant =
    view === 'application' && selectedApplication !== null && selectedApplication.active && canGrant;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: queryKeys.allGrants });
  }

  function close() {
    setDialog(null);
    setFormError(null);
  }

  const grantMutation = useMutation({
    mutationFn: ({ userId, applicationId }: { userId: string; applicationId: string }) =>
      createGrant({ userId, applicationId }),
    onSuccess: (result) => {
      refresh();
      close();
      showToast(STR.toast.accessGranted(result.grant.user.name, result.grant.application.name));
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : STR.errors.generic);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (grant: GrantPublic) => revokeGrant(grant.id),
    onSuccess: (result) => {
      refresh();
      close();
      showToast(
        STR.toast.accessRevoked(result.grant.user.name, result.grant.application.name),
        'warn',
      );
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  /**
   * The confirmation names a count computed from the data this screen already holds — an
   * AFFORDANCE, never the authority. The server recomputes `granted`/`alreadyHeld` itself
   * from the live table and the toast reports THAT real outcome, not the number shown before
   * the click.
   */
  const bulkGrantMutation = useMutation({
    mutationFn: (applicationId: string) => bulkGrant({ applicationId }),
    onSuccess: (result) => {
      refresh();
      close();
      showToast(STR.toast.bulkGranted(result.granted, result.alreadyHeld));
    },
    onError: (error) => {
      close();
      showToast(error instanceof ApiError ? error.message : STR.errors.generic, 'warn');
    },
  });

  const liveCountFor = (predicate: (grant: GrantPublic) => boolean): number =>
    allGrants.filter((grant) => grant.revokedAt === null && predicate(grant)).length;

  const subjectName =
    view === 'person' ? (selectedUser?.name ?? STR.common.dash) : (selectedApplication?.name ?? STR.common.dash);
  const subjectSub =
    view === 'person' ? (selectedUser?.email ?? '') : (selectedApplication?.baseUrl ?? '');
  const hasSubject = view === 'person' ? selectedUser !== null : selectedApplication !== null;

  return (
    <AdminShell surfaceId="grants">
      {loading ? <MasterDetailSkeleton /> : null}

      {loading ? null : (
        <div style={masterDetailStyle}>
          <div style={listPaneStyle}>
            <div style={listPaneHeaderStyle}>
              {grantsSummary === null ? null : (
                <div className="tx-subtle" style={eyebrowStyle}>
                  {STR.grants.meta(grantsSummary.live, grantsSummary.revoked)}
                </div>
              )}
              <div
                className="surface-2"
                role="group"
                aria-label={STR.grants.viewLabel}
                style={{
                  display: 'flex',
                  gap: 2,
                  padding: 3,
                  borderRadius: 9,
                  border: '1px solid var(--border)',
                }}
              >
                <button
                  type="button"
                  className="seg"
                  aria-pressed={view === 'person'}
                  onClick={() => {
                    setView('person');
                    setShowRevoked(false);
                  }}
                  style={{ flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 12, fontWeight: 650, cursor: 'pointer' }}
                >
                  {STR.grants.byPerson}
                </button>
                <button
                  type="button"
                  className="seg"
                  aria-pressed={view === 'application'}
                  onClick={() => {
                    setView('application');
                    setShowRevoked(false);
                  }}
                  style={{ flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 12, fontWeight: 650, cursor: 'pointer' }}
                >
                  {STR.grants.byApplication}
                </button>
              </div>
              <input
                className="field"
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                placeholder={
                  view === 'person' ? STR.grants.searchPeople : STR.grants.searchApplications
                }
                aria-label={STR.common.search}
                style={searchFieldStyle}
              />
              {(view === 'person' ? visibleUsers.length : visibleApplications.length) === 0 ? (
                <div style={{ textAlign: 'center', padding: '6px 0 2px' }}>
                  <span className="tx-subtle" style={{ fontSize: 12 }}>
                    {STR.common.noMatch}
                  </span>
                  <button
                    type="button"
                    className="tbtn"
                    onClick={() => {
                      setSearch('');
                    }}
                    style={{ ...rowActionStyle, marginLeft: 4 }}
                  >
                    {STR.common.clear}
                  </button>
                </div>
              ) : null}
            </div>

            <ul style={listContainerStyle}>
              {view === 'person'
                ? visibleUsers.map((user) => (
                    <li key={user.id} {...(user.active ? {} : { 'data-off': '' })}>
                      <button
                        type="button"
                        className="li"
                        aria-current={selectedUser?.id === user.id}
                        onClick={() => {
                          setPickedUserId(user.id);
                          setShowRevoked(false);
                        }}
                        style={listRowStyle}
                      >
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span className="nm" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                            {user.name}
                          </span>
                          <span
                            className="tx-subtle"
                            style={{
                              display: 'block',
                              fontSize: 11.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {user.email}
                          </span>
                        </span>
                        <Chip tone="neutral" size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {liveCountFor((grant) => grant.userId === user.id)}
                        </Chip>
                      </button>
                    </li>
                  ))
                : visibleApplications.map((application) => (
                    <li key={application.id}>
                      <button
                        type="button"
                        className="li"
                        aria-current={selectedApplication?.id === application.id}
                        onClick={() => {
                          setPickedApplicationId(application.id);
                          setShowRevoked(false);
                        }}
                        style={listRowStyle}
                      >
                        <Dot tone={healthOf(application.lastStatus).dot} />
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>
                            {application.name}
                          </span>
                          <span
                            className="tx-subtle"
                            style={{ display: 'block', fontSize: 11.5, ...monoStyle }}
                          >
                            {application.key}
                          </span>
                        </span>
                        <Chip tone="neutral" size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {liveCountFor((grant) => grant.applicationId === application.id)}
                        </Chip>
                      </button>
                    </li>
                  ))}
            </ul>

            <ListTruncationNotice
              loaded={view === 'person' ? users.length : applications.length}
              total={view === 'person' ? usersTotal : applicationsTotal}
            />
          </div>

          <div style={detailPaneStyle}>
            <div style={detailColumnStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={detailNameStyle}>{subjectName}</div>
                  <div className="tx-muted" style={{ fontSize: 13 }}>
                    {subjectSub}
                  </div>
                </div>
                <div
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  {canBulkGrant && selectedApplication !== null ? (
                    <button
                      type="button"
                      className="ghost"
                      style={primaryButtonStyle}
                      onClick={() => {
                        setDialog({ kind: 'bulkGrant', application: selectedApplication });
                      }}
                    >
                      {STR.grants.bulkGrantAction}
                    </button>
                  ) : null}
                  {hasSubject && canGrant ? (
                    <button
                      type="button"
                      className="prim"
                      style={primaryButtonStyle}
                      onClick={() => {
                        setFormError(null);
                        setDialog({ kind: 'grant' });
                      }}
                    >
                      {view === 'person' ? STR.grants.grantApplication : STR.grants.grantToPerson}
                    </button>
                  ) : null}
                  {hasSubject && !canGrant ? (
                    <span className="tx-subtle" style={{ fontSize: 12 }}>
                      {view === 'person' ? STR.grants.allAppsHeld : STR.grants.allPeopleHold}
                    </span>
                  ) : null}
                </div>
              </div>

              <Card>
                <div
                  className="tx-subtle"
                  style={{
                    ...eyebrowStyle,
                    padding: '12px 18px',
                    borderBottom: '1px solid var(--border)',
                    background: 'var(--surface-2)',
                  }}
                >
                  {view === 'person' ? STR.grants.columnApplication : STR.grants.columnPerson}
                </div>

                {!hasSubject ? (
                  <div
                    className="tx-subtle"
                    style={{ padding: '34px 16px', textAlign: 'center', fontSize: 13 }}
                  >
                    {view === 'person' ? STR.grants.noPeople : STR.grants.noApplications}
                  </div>
                ) : null}

                {hasSubject && liveGrants.length === 0 ? (
                  <div
                    className="tx-subtle"
                    style={{ padding: '34px 16px', textAlign: 'center', fontSize: 13 }}
                  >
                    {view === 'person' ? STR.grants.emptyForPerson : STR.grants.emptyForApplication}
                  </div>
                ) : null}

                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {liveGrants.map((grant) => (
                    <li
                      key={grant.id}
                      className="row"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        padding: '15px 18px',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                          {view === 'person' ? grant.application.name : grant.user.name}
                        </div>
                        <div className="tx-subtle" style={{ fontSize: 11.5 }}>
                          {view === 'person' ? grant.application.key : grant.user.email}
                        </div>
                      </div>
                      <div className="tx-muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                        {grantedLine(grant, names)}
                      </div>
                      <button
                        type="button"
                        className="tbtn tbtn-danger"
                        style={rowActionStyle}
                        onClick={() => {
                          setDialog({ kind: 'revoke', grant });
                        }}
                      >
                        {STR.common.revoke}
                      </button>
                    </li>
                  ))}
                </ul>

                {pastGrants.length > 0 ? (
                  <div
                    style={{
                      padding: '10px 15px',
                      background: 'var(--surface-2)',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    <button
                      type="button"
                      className="tbtn"
                      aria-expanded={showRevoked}
                      onClick={() => {
                        setShowRevoked(!showRevoked);
                      }}
                      style={quietButtonStyle}
                    >
                      {showRevoked
                        ? STR.grants.hideRevoked(pastGrants.length)
                        : STR.grants.showRevoked(pastGrants.length)}
                    </button>
                  </div>
                ) : null}

                {showRevoked && pastGrants.length > 0 ? (
                  <ul
                    style={{
                      listStyle: 'none',
                      margin: 0,
                      padding: 0,
                      background: 'var(--surface-2)',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    {pastGrants.map((grant) => (
                      <li
                        key={grant.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 13,
                          padding: '11px 15px',
                          borderBottom: '1px solid var(--border)',
                        }}
                      >
                        <span
                          className="bg-neutral-soft"
                          style={{ width: 3, alignSelf: 'stretch', borderRadius: 2, flex: 'none' }}
                          aria-hidden="true"
                        />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            className="tx-subtle"
                            style={{ fontSize: 13, fontWeight: 600, textDecoration: 'line-through' }}
                          >
                            {view === 'person' ? grant.application.name : grant.user.name}
                          </div>
                          <div className="tx-subtle" style={{ fontSize: 11.5 }}>
                            {grantedLine(grant, names)}
                          </div>
                        </div>
                        <Chip tone="neutral" style={{ whiteSpace: 'nowrap' }}>
                          {revokedLine(grant)}
                        </Chip>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Card>
            </div>
          </div>
        </div>
      )}

      {dialog?.kind === 'grant' ? (
        <GrantModal
          context={
            view === 'person'
              ? STR.modals.grant.contextForPerson(selectedUser?.name ?? '')
              : STR.modals.grant.contextForApplication(selectedApplication?.name ?? '')
          }
          label={view === 'person' ? STR.modals.grant.pickApplication : STR.modals.grant.pickPerson}
          options={
            view === 'person'
              ? grantableApplications.map((application) => ({
                  id: application.id,
                  label: `${application.name} — ${application.baseUrl}`,
                }))
              : grantableUsers.map((user) => ({
                  id: user.id,
                  label: `${user.name} — ${user.email}`,
                }))
          }
          error={formError}
          busy={grantMutation.isPending}
          onCancel={close}
          onSubmit={(id) => {
            if (id === '') {
              setFormError(STR.modals.grant.required);
              return;
            }
            if (view === 'person') {
              if (selectedUser === null) return;
              grantMutation.mutate({ userId: selectedUser.id, applicationId: id });
              return;
            }
            if (selectedApplication === null) return;
            grantMutation.mutate({ userId: id, applicationId: selectedApplication.id });
          }}
        />
      ) : null}

      {dialog?.kind === 'revoke' ? (
        <RevokeModal
          what={dialog.grant.application.name}
          who={dialog.grant.user.name}
          busy={revokeMutation.isPending}
          onCancel={close}
          onSubmit={() => {
            revokeMutation.mutate(dialog.grant);
          }}
        />
      ) : null}

      {dialog?.kind === 'bulkGrant' ? (
        <Modal
          title={STR.grants.bulkGrantTitle(dialog.application.name)}
          onClose={close}
          footer={
            <>
              <button type="button" className="ghost" style={modalButtonStyle} onClick={close}>
                {STR.common.cancel}
              </button>
              <button
                type="button"
                className="prim"
                style={modalButtonStyle}
                disabled={bulkGrantMutation.isPending}
                onClick={() => {
                  bulkGrantMutation.mutate(dialog.application.id);
                }}
              >
                {STR.grants.bulkGrantSubmit}
              </button>
            </>
          }
        >
          <ModalProse>
            <ModalParagraph>{STR.grants.bulkGrantBody(grantableUsers.length)}</ModalParagraph>
          </ModalProse>
        </Modal>
      ) : null}
    </AdminShell>
  );
}
